import React from "react";
import {DATE, DATE_LINK, NUMBER, NUMBER_LINK, STRING, STRING_LINK, CURRENCY, CURRENCY_LINK, PERCENT} from "../constants";

const USD_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
});

const NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
});

const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
});



export class Cell {

    constructor(columnDef, rawValue, rowIndex) {
        if (!columnDef) {
            throw new Error("Column definition is required for Cell. Check that your column(Def) count matches your row length");
        }
        if (rowIndex === undefined || rowIndex === null) {
            throw new Error("Row index is required for Cell.");
        }
        this.rawValue = rawValue;
        this.columnDef = columnDef; // ColumnDef instance
        this.rowIndex = rowIndex;
        this.elementId = this.getElementId();
        this.type = columnDef.type;
        this.isLink = columnDef.isLink;
        this.isNumber = columnDef.isNumber;
        this.displayValue = null;
        const isntPrimitiveNumber = (isNaN(this.rawValue) || typeof this.rawValue !== "number") && this.rawValue !== null;
        const isntLinkNumber = this.isLink && (isNaN(this.rawValue?.displayValue) || typeof this.rawValue?.displayValue !== "number") && this.rawValue?.displayValue !== null;
        const isntNumber = isntPrimitiveNumber && isntLinkNumber;
        if (this.isNumber && isntNumber) {
            console.warn(`Cell in column '${this.columnDef.displayName}' at row ${this.rowIndex} has a non-numeric value for a numeric column:`, this.rawValue, "which is a", typeof this.rawValue, ". We expect either a number or null.");
            throw new Error("Cell value is not a valid number for a numeric column.");
        }
        this._sortValue = null; // Cache for sort value
        this.length = this.isLink ? this.rawValue?.displayValue?.toString()?.length || 0 : this.getDisplayValue().length; // Length of the display value for sorting
    }

    getIndex() {
        if (isNaN(this.columnDef?.getIndex())) {
            console.error("Cell", this, "does not have a columnDef with an index set.");
            throw new Error("Cell does not have a columnDef with an index set.");
        }
        return this.columnDef.getIndex();
    }

    getElementId() {
        let elementId = [];
        const alphabet = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        for (let i =0; i<10; i++) {
            elementId.push(Math.floor(Math.random() * 61));
        }
        return elementId.join("");
    }

    shouldInclude() {
        // Check if the column has a filter function and apply it
        return !this.columnDef.options || !this.columnDef.options.shouldInclude || this.columnDef.options.shouldInclude();
    }

    getSortValue() {
        if (this._sortValue !== null) {
            return this._sortValue;
        }
        const rawValue = this.rawValue; // this.rawValue?.type === "a" ? this.rawValue?.props?.children : this.rawValue; // Handle special case for React elements
        if (this.columnDef.type === CURRENCY) {
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                this._sortValue = 0.;
            } else {
                this._sortValue = this.rawValue;
            }
        } else if (this.columnDef.type === CURRENCY_LINK) {
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                this._sortValue = 0.;
            } else if (isNaN(rawValue?.displayValue)) {
                this._sortValue = parseFloat(rawValue?.displayValue?.replace(/[^0-9.-]+/g, "")) || 0; // Convert currency string to float
            } else {
                this._sortValue = parseFloat(rawValue?.displayValue); // Convert currency string to float
            }
        } else if (this.columnDef.type === NUMBER || this.columnDef.type === PERCENT) {
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                this._sortValue = 0.;
            } else {
                this._sortValue = parseFloat(rawValue); // Convert number string to float
            }
        } else if (this.columnDef.type === NUMBER_LINK) {
            const displayValue = rawValue?.displayValue || null; // Handle case where displayValue is not present
            if (displayValue === null || displayValue === undefined || displayValue === "") {
                return 0;
            }
            if (isNaN(displayValue)) {
                this._sortValue = parseFloat(displayValue.replace(/[^0-9.-]+/g, "")) || 0; // Convert number string to float
            } else {
                this._sortValue = parseFloat(displayValue); // Convert number string to float
            }
        } else if (this.columnDef.type === DATE) {
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                this._sortValue = 0;
            } else {
                this._sortValue = new Date(rawValue).getTime(); // Convert date string to timestamp
            }
        } else if (this.columnDef.type === DATE_LINK) {
            const displayValue = rawValue?.displayValue || null; // Handle case where displayValue is not present
            if (displayValue === null || displayValue === undefined || displayValue === "") {
                this._sortValue = 0;
            } else {
                this._sortValue = new Date(displayValue).getTime(); // Convert date string to timestamp
            }
        } else if (this.columnDef.type === STRING) {
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                this._sortValue = "";
            } else {
                this._sortValue = rawValue.toString().toLowerCase(); // Convert string to lowercase for consistent sorting
            }
        } else if (this.columnDef.type === STRING_LINK) {
            const displayValue = rawValue?.displayValue || null; // Handle case where displayValue is not present
            if (displayValue === null || displayValue === undefined || displayValue === "") {
                this._sortValue = "";
            } else {
                this._sortValue = displayValue.toString().toLowerCase(); // Convert string to lowercase for consistent sorting
            }
        } else {
            this._sortValue = rawValue;
        }
        return this._sortValue; // Default to raw value if no sort function is defined
    }


    getDOMText() {
        if (!this.isLink) {
            return this.getDisplayValue();
        }
        return this.rawValue?.displayValue || "---";
    }


    getDisplayValue() {
        if (this.displayValue !== null) {
            return this.displayValue; // Return cached display value if available
        }

        if (this.isLink) {
            const displayValue = this.rawValue?.displayValue || null; // Fallback to placeholder if displayValue is not set
            if (displayValue === null || displayValue === undefined || displayValue === "") {
                this.displayValue = <a href="#" role="link">{"---"}</a>;
                return this.displayValue; // Return the placeholder link
            }

            const props = this.rawValue?.props ? {role: "link", ...this.rawValue.props} : {};
            let innerText = displayValue; // Use displayValue as the inner text for the link
            switch (this.columnDef.type) {
            case STRING_LINK:
                innerText = innerText.toString();
                break
            case CURRENCY_LINK:
                innerText = USD_CURRENCY_FORMATTER.format(innerText);
                break;
            case NUMBER_LINK:
                innerText = NUMBER_FORMATTER.format(innerText);
                break;
            case PERCENT:
                innerText = PERCENT_FORMATTER.format(innerText); // Format as percentage
                break;
            case DATE_LINK:
                innerText = new Date(innerText).toLocaleDateString("en-US");
                break;
            }

            this.displayValue = <a {...props}>{innerText}</a>; // Create a React element for links
            return this.displayValue; // Return the React element for links
        }

        switch(this.columnDef.type) {
        case CURRENCY:
            this.displayValue = USD_CURRENCY_FORMATTER.format(this.rawValue);
            break;
        case NUMBER:
            this.displayValue = NUMBER_FORMATTER.format(this.rawValue);
            break;
        case DATE:
            this.displayValue = new Date(this.rawValue).toLocaleDateString("en-US");
            break;
        case STRING:
            this.displayValue = this.rawValue || "---"; // Return raw value or placeholder if empty
            break;
        case PERCENT:
            this.displayValue = PERCENT_FORMATTER.format(this.rawValue); // Format as percentage
            break;
        }

        return this.displayValue; // Return the computed display value
    }

    key() {
        return `cell-${this.rowIndex}-${this.columnDef.getIndex()}-${this.columnDef.displayName}`;
    }

    getLength() {
        return this.length;
    }

    renderAsDOMElement(compact, aggregatedColumnDefs) {
        let className = compact ? "canold-table-td-compact" : "canold-table-td";
        const props = {
            title: this.getDOMText(),
            role: "canold-table-cell",
            id: this.elementId,
            key: this.key(),
            className: className
        }

        if (this.columnDef.style) {
            props.style = this.columnDef.style;
        }

        const td = document.createElement("td");
        td.setAttribute("title", this.getDOMText());
        td.setAttribute("role", "canold-table-cell");
        td.setAttribute("id", this.elementId);
        td.setAttribute("key", this.key());
        td.className = className + " computing";
        if (this.columnDef.style) {
            td.setAttribute("style", JSON.stringify(this.columnDef.style)); // Set style as a JSON string
        }
        if (this.columnDef.isHidden()) {
            return td; // Return empty td if hidden
        }
        td.innerHTML = this.isLink ? (this.rawValue?.displayValue || "---") : this.getDisplayValue(); // Set inner HTML to the display value
        return td; // Return the constructed td element
    }

    render(compact, aggregatedColumnDefs, collapsed) {
        // If you update this method make sure you update renderAsDOMElement as well
        let className = compact ? "canold-table-td-compact" : "canold-table-td";
        const props = {
            title: this.getDOMText(),
            role: "canold-table-cell",
            id: this.elementId,
            key: this.key(),
            className: className
        }
        if (this.columnDef.style) {
            props.style = this.columnDef.style;
        }
        if (this.columnDef.isHidden()) {
            return <td {...props}></td>;
        }
        if (collapsed) {
            props.className += " collapsed"; // Add collapsed class if collapsed
            return <td {...props}/>
        }
        return <td {...props}>{this.getDisplayValue()}</td>;
    }

}
